import React, { useCallback } from "react";
import { connect } from "react-redux";

import Tabs from "antd/es/tabs";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";

import Feature from "@mapmycustomers/shared/enum/Feature";
import Organization from "@mapmycustomers/shared/types/Organization";

import Path from "@app/enum/Path";
import SettingPath, { OrganizationDetailsTabs } from "@app/enum/settings/SettingPath";
import DefaultColumns from "@app/scene/settings/component/OrganizationDetails/component/DefaultColumns";
import { getOrganization, isOrganizationUpdating, isResourcesEnabled } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useFeatures from "@app/util/hook/useFeatures";

import SettingPage from "../common/SettingPage";

import Activities from "./component/Activities";
import DataSettings from "./component/DataSettings";
import Deals from "./component/Deals";
import General from "./component/General";
import Leaderboard from "./component/Leaderboard";
import LeadGeneration from "./component/LeadGeneration";
import Map from "./component/Map";
import MileageTracking from "./component/MileageTracking";
import Resources from "./component/Resources";
import styles from "./OrganizationDetails.module.scss";
import usePageControllers from "./util/usePageControllers";

const TabNamesForAnalytics: Record<OrganizationDetailsTabs, string> = {
  [OrganizationDetailsTabs.ACTIVITIES]: "Activities tab",
  [OrganizationDetailsTabs.DATA]: "Data tab",
  [OrganizationDetailsTabs.DEALS]: "Deals tab",
  [OrganizationDetailsTabs.DEFAULT_COLUMNS]: "Default Columns tab",
  [OrganizationDetailsTabs.GENERAL]: "General tab",
  [OrganizationDetailsTabs.LEAD_GENERATION]: "Lead finder tab",
  [OrganizationDetailsTabs.LEADERBOARD]: "Stack Rank tab",
  [OrganizationDetailsTabs.MAP]: "Map tab",
  [OrganizationDetailsTabs.MILEAGE_TRACKING]: "Mileage Tracking tab",
  [OrganizationDetailsTabs.RESOURCES]: "Resources tab",
};

interface Props {
  isOrganizationUpdating: boolean;
  organization: Organization | undefined;
  resourcesEnabled: boolean;
}

const OrganizationDetails: React.FC<Props> = ({
  isOrganizationUpdating,
  organization,
  resourcesEnabled,
}) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("Org Details", useAnalytics());
  const history = useHistory();
  const { tab = OrganizationDetailsTabs.GENERAL } = useParams<{ tab: OrganizationDetailsTabs }>();
  const { form, handleUndo, isDirty, setDirty, unsetDirty } = usePageControllers(tab);

  const handleTabClick = useCallback(
    (key) => {
      history.push(`${Path.SETTINGS}/${SettingPath.ORGANIZATION_DETAILS}/${key}`);
      analytics.clicked([TabNamesForAnalytics[key as OrganizationDetailsTabs]]);
    },
    [analytics, history]
  );

  const { hasFeature } = useFeatures();

  return (
    <SettingPage
      changesSaving={isOrganizationUpdating}
      hasChanges={isDirty}
      onSaveChanges={form.submit}
      onUnDo={handleUndo}
      shouldManualClearOnLeave
      title={intl.formatMessage({
        id: "settings.organizationDetails.title",
        defaultMessage: "Organization Preferences",
        description: "Title of settings organization details page",
      })}
    >
      <AnalyticsContext.Provider value={analytics}>
        <Tabs activeKey={tab} className={styles.tabs} onTabClick={handleTabClick}>
          <Tabs.TabPane
            key={OrganizationDetailsTabs.GENERAL}
            tab={intl.formatMessage({
              id: "settings.organizationDetails.general",
              defaultMessage: "General",
              description: "Title of organization details general tab",
            })}
          >
            <General form={form} setDirty={setDirty} unsetDirty={unsetDirty} />
          </Tabs.TabPane>
          <Tabs.TabPane
            key={OrganizationDetailsTabs.ACTIVITIES}
            tab={intl.formatMessage({
              id: "settings.organizationDetails.activities",
              defaultMessage: "Activities",
              description: "Title of organization details activities tab",
            })}
          >
            <Activities form={form} setDirty={setDirty} unsetDirty={unsetDirty} />
          </Tabs.TabPane>
          {hasFeature(Feature.MAP) && (
            <Tabs.TabPane
              key={OrganizationDetailsTabs.MAP}
              tab={intl.formatMessage({
                id: "settings.organizationDetails.map",
                defaultMessage: "Map",
                description: "Title of organization details map tab",
              })}
            >
              <Map form={form} setDirty={setDirty} unsetDirty={unsetDirty} />
            </Tabs.TabPane>
          )}
          <Tabs.TabPane
            key={OrganizationDetailsTabs.LEAD_GENERATION}
            tab={intl.formatMessage({
              id: "settings.organizationDetails.leadFinder",
              defaultMessage: "Lead Finder",
              description: "Title of organization details lead finder tab",
            })}
          >
            <LeadGeneration form={form} setDirty={setDirty} unsetDirty={unsetDirty} />
          </Tabs.TabPane>
          <Tabs.TabPane
            key={OrganizationDetailsTabs.LEADERBOARD}
            tab={intl.formatMessage({
              id: "settings.organizationDetails.stackRank",
              defaultMessage: "Stack Rank",
              description: "Title of stack rank lead generation tab",
            })}
          >
            <Leaderboard form={form} setDirty={setDirty} unsetDirty={unsetDirty} />
          </Tabs.TabPane>
          {organization?.addOnServices.dupeCheck && (
            <Tabs.TabPane
              key={OrganizationDetailsTabs.DATA}
              tab={intl.formatMessage({
                id: "settings.organizationDetails.data",
                defaultMessage: "Data",
                description: "Title of Data tab",
              })}
            >
              <DataSettings form={form} onHasChanges={setDirty} onSaved={unsetDirty} />
            </Tabs.TabPane>
          )}
          <Tabs.TabPane
            key={OrganizationDetailsTabs.DEFAULT_COLUMNS}
            tab={intl.formatMessage({
              id: "settings.organizationDetails.defaultColumns",
              defaultMessage: "Default Columns",
              description: "Title of Default Columns tab in Org preferences",
            })}
          >
            <DefaultColumns form={form} onHasChanges={setDirty} onSaved={unsetDirty} />
          </Tabs.TabPane>
          <Tabs.TabPane
            key={OrganizationDetailsTabs.DEALS}
            tab={intl.formatMessage({
              id: "settings.organizationDetails.deals",
              defaultMessage: "Deals",
              description: "Title of Deals tab in Org preferences",
            })}
          >
            <Deals form={form} setDirty={setDirty} unsetDirty={unsetDirty} />
          </Tabs.TabPane>
          <Tabs.TabPane
            key={OrganizationDetailsTabs.MILEAGE_TRACKING}
            tab={intl.formatMessage({
              id: "settings.organizationDetails.mileageTracking",
              defaultMessage: "Mileage Tracking",
              description: "Title of Mileage Tracking tab",
            })}
          >
            <MileageTracking form={form} onHasChanges={setDirty} onSaved={unsetDirty} />
          </Tabs.TabPane>
          {resourcesEnabled && (
            <Tabs.TabPane
              key={OrganizationDetailsTabs.RESOURCES}
              tab={intl.formatMessage({
                id: "settings.organizationDetails.resources",
                defaultMessage: "Resources",
                description: "Title of Resources tab",
              })}
            >
              <Resources form={form} onHasChanges={setDirty} onSaved={unsetDirty} />
            </Tabs.TabPane>
          )}
        </Tabs>
      </AnalyticsContext.Provider>
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOrganizationUpdating: isOrganizationUpdating(state),
  organization: getOrganization(state),
  resourcesEnabled: isResourcesEnabled(state),
});

export default connect(mapStateToProps)(OrganizationDetails);
